/**
 * PersonalityTraitsEditor Component
 * 5 slider: Creativity, Formality, Humor, Detail, Confidence (0-100)
 * Real-time preview of personality impact
 */

import { Briefcase, FileText, Lightbulb, Smile, TrendingUp } from 'lucide-react';

interface PersonalityTraits {
  creativity: number;
  formality: number;
  humor: number;
  detail: number;
  confidence: number;
}

interface PersonalityTraitsEditorProps {
  traits: PersonalityTraits;
  onChange: (trait: keyof PersonalityTraits, value: number) => void;
}

const TRAIT_CONFIG = {
  creativity: {
    label: 'Yaraticilik',
    icon: Lightbulb,
    color: '#f59e0b',
    lowLabel: 'Muhafazakar',
    highLabel: 'Yaratici',
    description: 'Yaratici cozumler mi, yoksa kanıtlanmis yontemler mi?'
  },
  formality: {
    label: 'Formallik',
    icon: Briefcase,
    color: '#3b82f6',
    lowLabel: 'Samimi',
    highLabel: 'Profesyonel',
    description: 'Gunluk dil mi, yoksa resmi iletisim mi?'
  },
  humor: {
    label: 'Mizah',
    icon: Smile,
    color: '#ec4899',
    lowLabel: 'Ciddi',
    highLabel: 'Eglenceli',
    description: 'Ciddi ton mu, yoksa espirili yaklasim mi?'
  },
  detail: {
    label: 'Detay Seviyesi',
    icon: FileText,
    color: '#8b5cf6',
    lowLabel: 'Ozet',
    highLabel: 'Detayli',
    description: 'Kisa ve oz mu, yoksa kapsamli aciklamalar mi?'
  },
  confidence: {
    label: 'Guven',
    icon: TrendingUp,
    color: '#10b981',
    lowLabel: 'Temkinli',
    highLabel: 'Kararlı',
    description: 'Tereddutlu mu, yoksa emin ifadeler mi?'
  }
};

export default function PersonalityTraitsEditor({ traits, onChange }: PersonalityTraitsEditorProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {/* Traits Sliders */}
      {(Object.keys(TRAIT_CONFIG) as Array<keyof PersonalityTraits>).map(traitKey => {
        const config = TRAIT_CONFIG[traitKey];
        const Icon = config.icon;
        const value = traits[traitKey];

        return (
          <div key={traitKey} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: `${config.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Icon size={20} color={config.color} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1a202c',
                    margin: 0
                  }}>{config.label}</h4>
                  <span style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: config.color
                  }}>{value}</span>
                </div>
                <p style={{
                  fontSize: '13px',
                  color: '#64748b',
                  margin: '4px 0 0 0'
                }}>{config.description}</p>
              </div>
            </div>

            {/* Slider */}
            <div style={{ paddingLeft: '52px' }}>
              <input
                type="range"
                min="0"
                max="100"
                value={value}
                onChange={(e) => onChange(traitKey, parseInt(e.target.value))}
                style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  background: `linear-gradient(to right, ${config.color}30 0%, ${config.color}30 ${value}%, #e2e8f0 ${value}%, #e2e8f0 100%)`,
                  outline: 'none',
                  cursor: 'pointer',
                  WebkitAppearance: 'none',
                  appearance: 'none'
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '8px'
              }}>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>{config.lowLabel}</span>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>{config.highLabel}</span>
              </div>
            </div>
          </div>
        );
      })}

      {/* Preview Section */}
      <div style={{
        marginTop: '16px',
        padding: '20px',
        borderRadius: '12px',
        background: '#f8fafc',
        border: '1px solid #e2e8f0'
      }}>
        <h4 style={{
          fontSize: '14px',
          fontWeight: '600',
          color: '#1a202c',
          marginBottom: '12px'
        }}>Kisilik Onizlemesi</h4>
        <p style={{
          fontSize: '13px',
          color: '#64748b',
          lineHeight: '1.6',
          margin: 0
        }}>
          {getPersonalityPreview(traits)}
        </p>
      </div>
    </div>
  );
}

// Kisilik onizleme metni olustur
function getPersonalityPreview(traits: PersonalityTraits): string {
  const parts: string[] = [];

  // Creativity
  if (traits.creativity > 70) {
    parts.push('yaratici ve yenilikci cozumler sunar');
  } else if (traits.creativity < 30) {
    parts.push('kanitlanmis ve guvenilir yontemleri tercih eder');
  } else {
    parts.push('dengeli ve pragmatik yaklasimlar kullanir');
  }

  // Formality
  if (traits.formality > 70) {
    parts.push('profesyonel ve resmi bir dil kullanir');
  } else if (traits.formality < 30) {
    parts.push('samimi ve gunluk bir dil kullanir');
  } else {
    parts.push('uygun bir formallik seviyesi kullanir');
  }

  // Humor
  if (traits.humor > 70) {
    parts.push('espirili ve eglenceli bir ton benimser');
  } else if (traits.humor < 30) {
    parts.push('ciddi ve odakli bir ton benimser');
  }

  // Detail
  if (traits.detail > 70) {
    parts.push('kapsamli ve detayli aciklamalar yapar');
  } else if (traits.detail < 30) {
    parts.push('kisa ve oz aciklamalar yapar');
  } else {
    parts.push('yeterli detay seviyesinde aciklamalar yapar');
  }

  // Confidence
  if (traits.confidence > 70) {
    parts.push('emin ve kararli ifadeler kullanir');
  } else if (traits.confidence < 30) {
    parts.push('temkinli ve sorgulayici ifadeler kullanir');
  } else {
    parts.push('dengeli ve makul ifadeler kullanir');
  }

  return `Bu agent ${parts.join(', ')}.`;
}
